import { QueryClient } from "@tanstack/react-query";
import { ApiError } from "./api";

/**
 * These defaults were chosen when Aurora Serverless ran at minCapacity 0 and a first request after an
 * idle stretch took a measured ~20s to resume. A refetch was genuinely expensive, so the cache was
 * tuned to avoid one at almost any cost.
 *
 * Aurora is gone. On DynamoDB the same request is ~1.0s cold and ~0.15s warm, so a refetch is now
 * cheap enough that showing stale data is the worse trade -- a coach who publishes a plan in one tab
 * and switches back to another should not be looking at a minute-old list.
 *
 * `staleTime` stays non-zero (nothing here changes second to second, and messages arrive over the
 * WebSocket rather than by polling), but it is short enough that a refetch on focus corrects anything
 * that moved.
 */
export const DEFAULT_STALE_TIME_MS = 15_000;

/**
 * Retries only what a retry could actually fix.
 *
 * 401/403 mean the session is gone or the caller isn't allowed, and 404 means the thing isn't there --
 * none of those change by asking again, so retrying just delays the error the user needs to see. A 409
 * is a genuine conflict with someone else's write and needs a human decision, not a race.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && [401, 403, 404, 409].includes(error.status)) return false;
  return failureCount < 2;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: DEFAULT_STALE_TIME_MS,
        // On again now that a refetch costs ~150ms rather than a 20-second cluster resume. This is what
        // makes coming back to a tab show current data instead of whatever was there when you left.
        refetchOnWindowFocus: true,
        retry: shouldRetry,
      },
      mutations: {
        retry: false,
      },
    },
  });
}
